import { createContext, useContext } from 'react';

/** Which relationship a navigation target has to the current node instance. */
export type NodeNavigationKind = 'child' | 'parent' | 'spawned' | 'originating' | 'previous';

export interface NodeNavigation {
  /**
   * Navigate to a related node instance. `kind` lets a provider decide how to
   * route (e.g. in-tree soft-swap vs a new tab); the default reproduces the
   * legacy per-kind window.open behaviour.
   */
  readonly open: (id: number | string, kind: NodeNavigationKind) => void;
}

/**
 * Default navigation: open in a new tab, exactly as the instance viewer did
 * before this context existed. child/spawned/originating open the instance
 * viewer; parent opens the node-instances list focused on the parent. So an
 * InstanceViewer rendered without a provider behaves identically to before.
 */
const openInNewTab = (id: number | string, kind: NodeNavigationKind) => {
  if (kind === 'parent') {
    const params = new URLSearchParams();
    params.set('node_instance_id', String(id));
    params.set('expanded', String(id));
    params.set('bottom_panel_id', String(id));
    window.open(`/automation/node_instances?${params.toString()}`, '_blank', 'noopener, noreferrer');
    return;
  }
  window.open(`/instance_viewer/${id}`, '_blank', 'noopener, noreferrer');
};

export const NodeNavigationContext = createContext<NodeNavigation>({
  open: openInNewTab,
});

export const useNodeNavigation = (): NodeNavigation => useContext(NodeNavigationContext);
